/**
 * Subscription misuse detector — pure logic, no side effects.
 *
 * analyzeTemplates(templates) returns:
 *   { score, reasons, fingerprint, groups }
 *
 * Templates should be objects with at least: { id, name, items? }
 * where items is an optional array of { inventory_item_id, location? } objects.
 */

// ─── Constants ────────────────────────────────────────────────────────────────

const PURPOSE_WORDS = [
  'end of period', 'eop', 'inventory', 'count', 'order', 'ordering',
  'weekly', 'monthly', 'vendor', 'bar', 'food', 'beverage', 'wine',
  'liquor',
];

const DATE_PATTERNS = [
  // Month names
  /\b(january|february|march|april|may|june|july|august|september|october|november|december)\b/,
  /\b(jan|feb|mar|apr|jun|jul|aug|sep|oct|nov|dec)\b/,
  // Weekdays
  /\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/,
  /\b(mon|tue|wed|thu|fri|sat|sun)\b/,
  // Years
  /\b(20\d{2}|19\d{2})\b/,
  // Quarters
  /\bq[1-4]\b/,
  /\bfy\d{4}\b/,
  // Numeric dates like 4/15, 4-15, etc. (pre-normalization, slashes/dashes intact)
  /\b\d{1,2}[\/\-]\d{1,2}([\/\-]\d{2,4})?\b/,
  // Same pattern post-normalization: slashes/dashes stripped to spaces → "4 15"
  /\b\d{1,2}\s+\d{1,2}(\s+\d{2,4})?\b/,
  // Week numbers
  /\bwk?\s*\d{1,2}\b/,
  // Period numbers like "period 3", "p3"
  /\bperiod\s+\d+\b/,
  /\bp\d{1,2}\b/,
];

const TASK_WORDS = [
  'food', 'beverage', 'liquor', 'wine', 'cleaning', 'dry goods', 'supplies',
  'walk-in', 'walkin', 'freezer', 'dry storage', 'drystorage',
];

const LOCATION_BONUS_WORDS = [
  'store', 'location', 'branch', 'unit', 'east', 'west', 'north', 'south',
  'downtown', 'uptown',
];

const COUNT_ZONE_WORDS = ['bar', 'walk in', 'walkin', 'freezer', 'dry storage', 'drystorage'];

// ─── Normalization ─────────────────────────────────────────────────────────────

function normalize(name) {
  let s = (name || '').toLowerCase();
  // Expand EOP abbreviation
  s = s.replace(/\beop\b/g, 'end of period');
  // Strip punctuation except spaces and alphanumeric
  s = s.replace(/[^a-z0-9\s]/g, ' ');
  // Collapse whitespace
  s = s.replace(/\s+/g, ' ').trim();
  return s;
}

// ─── Purpose extraction ────────────────────────────────────────────────────────

function extractPurposeAndQualifier(normalizedName) {
  let s = normalizedName;

  // Try to strip purpose words from the leading or trailing edge (repeat until stable)
  let changed = true;
  while (changed) {
    changed = false;
    for (const pw of PURPOSE_WORDS) {
      // Leading
      const leadingRe = new RegExp(`^${pw.replace(/\s+/g, '\\s+')}\\s*`);
      if (leadingRe.test(s)) {
        s = s.replace(leadingRe, '').trim();
        changed = true;
        break;
      }
      // Trailing
      const trailingRe = new RegExp(`\\s*${pw.replace(/\s+/g, '\\s+')}$`);
      if (trailingRe.test(s)) {
        s = s.replace(trailingRe, '').trim();
        changed = true;
        break;
      }
    }
  }

  // What's left is the qualifier
  return s;
}

function extractPurpose(normalizedName) {
  // Return the leading purpose keyword found
  for (const pw of PURPOSE_WORDS) {
    const re = new RegExp(`^${pw.replace(/\s+/g, '\\s+')}`);
    if (re.test(normalizedName)) return pw;
  }
  // Try trailing
  for (const pw of PURPOSE_WORDS) {
    const re = new RegExp(`${pw.replace(/\s+/g, '\\s+')}$`);
    if (re.test(normalizedName)) return pw;
  }
  return null;
}

// ─── Qualifier classification ──────────────────────────────────────────────────

function classifyQualifier(qualifier) {
  if (!qualifier) return 'empty';

  // Date-like check
  for (const pattern of DATE_PATTERNS) {
    if (pattern.test(qualifier)) return 'date-like';
  }

  // Task-like check
  for (const tw of TASK_WORDS) {
    if (qualifier.includes(tw)) return 'task-like';
  }

  // Anything else is location-like
  return 'location-like';
}

// ─── Fingerprint ───────────────────────────────────────────────────────────────

function stableHash(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
    hash = hash >>> 0; // convert to unsigned 32-bit
  }
  return hash.toString(36);
}

function computeFingerprint(templateIds) {
  const sorted = [...templateIds].sort().join('|');
  return stableHash(sorted);
}

// ─── Item overlap ──────────────────────────────────────────────────────────────

function computeItemOverlapRatio(itemsA, itemsB) {
  if (!itemsA || !itemsB || itemsA.length === 0 || itemsB.length === 0) return 0;
  const setA = new Set(itemsA.map((i) => i.inventory_item_id || i.id).filter(Boolean));
  const setB = new Set(itemsB.map((i) => i.inventory_item_id || i.id).filter(Boolean));
  if (setA.size === 0 || setB.size === 0) return 0;
  let overlap = 0;
  for (const id of setA) {
    if (setB.has(id)) overlap++;
  }
  const smaller = Math.min(setA.size, setB.size);
  return overlap / smaller;
}

// ─── Zone detection ────────────────────────────────────────────────────────────

function detectCountZones(template) {
  const items = template.items || [];
  const zones = new Set();
  for (const item of items) {
    const loc = normalize(item.location || item.item_location || '');
    for (const zone of COUNT_ZONE_WORDS) {
      if (loc.includes(zone)) zones.add(zone);
    }
  }
  // Also check the template name itself
  const name = normalize(template.name || '');
  for (const zone of COUNT_ZONE_WORDS) {
    if (name.includes(zone)) zones.add(zone);
  }
  return zones;
}

// ─── Main analyzer ─────────────────────────────────────────────────────────────

/**
 * Analyze a set of templates for possible multi-location misuse patterns.
 *
 * @param {Array} templates — array of template objects { id, name, items? }
 * @returns {{ score: number, reasons: string[], fingerprint: string, groups: Array[] }}
 */
export function analyzeTemplates(templates) {
  if (!templates || templates.length < 2) {
    return { score: 0, reasons: [], fingerprint: '', groups: [] };
  }

  // Normalize each template
  const enriched = templates.map((t) => {
    const norm = normalize(t.name || '');
    const purpose = extractPurpose(norm);
    const qualifier = extractPurposeAndQualifier(norm);
    const qualifierClass = classifyQualifier(qualifier);
    return { ...t, norm, purpose, qualifier, qualifierClass, zones: detectCountZones(t) };
  });

  // Group by purpose — find pairs/groups that share a purpose but have different qualifiers
  const byPurpose = {};
  for (const t of enriched) {
    const key = t.purpose || '__none__';
    if (!byPurpose[key]) byPurpose[key] = [];
    byPurpose[key].push(t);
  }

  let bestScore = 0;
  let bestReasons = [];
  let bestGroup = [];
  // All pairs with a positive score, sorted best-first.
  // Each entry: { score, reasons, pair: [a, b], fingerprint }
  // Callers can use this to find the best suspicious pair involving a specific template.
  const allPairs = [];

  for (const [purpose, group] of Object.entries(byPurpose)) {
    if (group.length < 2) continue;

    // Check all pairs in the group
    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        const a = group[i];
        const b = group[j];

        // Skip if qualifiers are identical
        if (a.qualifier === b.qualifier) continue;

        let score = 0;
        const reasons = [];

        // +3 same purpose with different non-date qualifiers
        const aIsDate = a.qualifierClass === 'date-like';
        const bIsDate = b.qualifierClass === 'date-like';

        if (!aIsDate && !bIsDate) {
          score += 3;
          reasons.push(`Same purpose ("${purpose || 'counting'}") with different qualifiers ("${a.qualifier}" vs "${b.qualifier}")`);
        }

        // +2 both EOP
        const aIsEop = a.norm.includes('end of period');
        const bIsEop = b.norm.includes('end of period');
        if (aIsEop && bIsEop) {
          score += 2;
          reasons.push('Both templates are End of Period templates');
        }

        // +2 item overlap > 40%
        const overlapRatio = computeItemOverlapRatio(a.items, b.items);
        if (overlapRatio > 0.4) {
          score += 2;
          reasons.push(`High item overlap (${Math.round(overlapRatio * 100)}% of items shared)`);
        }

        // +2 repeated count zones
        const sharedZones = [...a.zones].filter((z) => b.zones.has(z));
        if (sharedZones.length >= 2) {
          score += 2;
          reasons.push(`Repeated count zones: ${sharedZones.join(', ')}`);
        }

        // +2 location-like qualifier words
        const aIsLocation = a.qualifierClass === 'location-like';
        const bIsLocation = b.qualifierClass === 'location-like';
        if (aIsLocation || bIsLocation) {
          score += 2;
          reasons.push('Qualifier words suggest physical locations');
        }

        // +1 explicit location indicator word (store/branch/north/south/downtown/etc.)
        const aHasBonus = LOCATION_BONUS_WORDS.some((w) => a.qualifier.includes(w));
        const bHasBonus = LOCATION_BONUS_WORDS.some((w) => b.qualifier.includes(w));
        if (aHasBonus || bHasBonus) {
          score += 1;
          reasons.push('Qualifier contains strong location indicator (store/branch/north/south/etc.)');
        }

        // -3 date-like qualifier
        if (aIsDate || bIsDate) {
          score -= 3;
          reasons.push('One or both qualifiers appear date-based (not location)');
        }

        // -2 task-like qualifier
        const aIsTask = a.qualifierClass === 'task-like';
        const bIsTask = b.qualifierClass === 'task-like';
        if (aIsTask || bIsTask) {
          score -= 2;
          reasons.push('One or both qualifiers appear task-based (not location)');
        }

        const finalScore = Math.max(0, score);
        if (finalScore > 0) {
          allPairs.push({
            score: finalScore,
            reasons: [...reasons],
            pair: [a, b],
            fingerprint: computeFingerprint([a.id, b.id]),
          });
        }

        if (score > bestScore) {
          bestScore = score;
          bestReasons = reasons;
          bestGroup = [a, b];
        }
      }
    }
  }

  // Sort all pairs best-first so callers can quickly find the most suspicious match.
  allPairs.sort((x, y) => y.score - x.score);

  const fingerprint = bestGroup.length >= 2
    ? computeFingerprint(bestGroup.map((t) => t.id))
    : '';

  return {
    score: Math.max(0, bestScore),
    reasons: bestReasons,
    fingerprint,
    groups: bestGroup.length >= 2 ? [bestGroup] : [],
    allPairs,
  };
}

// ─── Suppression helpers (per-user, per-store) ─────────────────────────────────

/**
 * Read the current user's ID from localStorage for scoping suppression.
 * Returns 'anon' if no user is found.
 */
function getCurrentUserId() {
  try {
    const stored = localStorage.getItem('currentUser');
    if (!stored) return 'anon';
    const parsed = JSON.parse(stored);
    return parsed?.id || 'anon';
  } catch (_) {
    return 'anon';
  }
}

function suppressionKey(storeId) {
  const userId = getCurrentUserId();
  return `misuseSuppressedFingerprints:${userId}:${storeId}`;
}

/**
 * Check if a fingerprint is suppressed for the current user + store.
 * Uses localStorage key `misuseSuppressedFingerprints:<userId>:<storeId>`.
 */
export function isFingerprintSuppressed(storeId, fingerprint) {
  if (!storeId || !fingerprint) return false;
  try {
    const stored = JSON.parse(localStorage.getItem(suppressionKey(storeId)) || '[]');
    return stored.includes(fingerprint);
  } catch (_) {
    return false;
  }
}

/**
 * Suppress a fingerprint for the current user + store so future detections
 * are skipped for this browser session/user combination.
 */
export function suppressFingerprint(storeId, fingerprint) {
  if (!storeId || !fingerprint) return;
  try {
    const key = suppressionKey(storeId);
    const stored = JSON.parse(localStorage.getItem(key) || '[]');
    if (!stored.includes(fingerprint)) {
      stored.push(fingerprint);
      localStorage.setItem(key, JSON.stringify(stored));
    }
  } catch (_) {}
}
